import { useEffect, useMemo, useRef, useSyncExternalStore, type CSSProperties } from 'react';
import type { LeafViewComponent } from '@customerio/dryad-react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Box, Button, InlineStack, Link, Select, Stack, Text } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { peopleFilterBuilder, type PeopleFilterBuilderMeta, type TypeOptionGroup } from './builder';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { ATTRIBUTE_CONDITION_TYPE, isAttributeLocked } from './attribute-condition';
import { AttributeRow } from './attribute-row';
import { DeviceAttributeRow } from './device-attribute-row';
import { EventRow } from './event-row';
import { MessageRow } from './message-row';
import { NewsletterRecipientsRow } from './newsletter-recipients-row';
import { OptOutRow } from './opt-out-row';
import { SegAttrRow } from './seg-attr-row';
import { SegmentsRow } from './segments-row';
import { defaultOptOutChannel, hasAnyOptOutChannel } from './opt-out-channel';
import {
  ConditionType,
  PEOPLE_SEARCH_TYPE_LABELS,
  peopleSearchConditionTypeOf,
  type PeopleSearchConditionType,
} from './condition-types';
import { PEOPLE_FILTER_FORMAT } from './people-filter-format';
import { normalizePeopleFilterWire } from './people-filter-format';
import { decodePeopleFiltersParam } from './filters-param';
import type { JsonValue } from './wire-types';

peopleFilterBuilder.registerView(ATTRIBUTE_CONDITION_TYPE, AttributeRow);
peopleFilterBuilder.registerView(ConditionType.DeviceAttribute, DeviceAttributeRow);
peopleFilterBuilder.registerView(ConditionType.Event, EventRow);
peopleFilterBuilder.registerView(ConditionType.Message, MessageRow);
peopleFilterBuilder.registerView(ConditionType.NewsletterRecipients, NewsletterRecipientsRow);
peopleFilterBuilder.registerView(ConditionType.OptOut, OptOutRow);
peopleFilterBuilder.registerView(ConditionType.SegAttr, SegAttrRow);
peopleFilterBuilder.registerView(ConditionType.SegmentsNegatable, SegmentsRow);

/**
 * The condition types with a ported row. A type outside this set never
 * enters the add menu.
 */
const AVAILABLE_ROW_TYPES: PeopleSearchConditionType[] = [
  ATTRIBUTE_CONDITION_TYPE,
  ConditionType.DeviceAttribute,
  ConditionType.Event,
  ConditionType.Message,
  ConditionType.NewsletterRecipients,
  ConditionType.OptOut,
  ConditionType.SegmentsNegatable,
];

const GROUP_OPERATOR_OPTIONS = [
  { value: 'and', label: 'All' },
  { value: 'or', label: 'At least one' },
];

const logicalOperatorLabelStyle = {
  display: 'inline-block',
  padding: `${themeVars.space['050']} ${themeVars.space['075']}`,
  textTransform: 'uppercase',
  fontSize: '0.625rem',
  fontWeight: themeVars['font-weight'].bold,
  color: themeVars.color['text-subtle'],
  backgroundColor: themeVars.color['surface-base'],
  border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
  borderRadius: themeVars.radius['4'],
  lineHeight: 1.5,
} satisfies CSSProperties;

// The fixed gutter the AND/OR labels sit in, so rows stay aligned whether or
// not they carry one.
const operatorGutterStyle = {
  width: '48px',
  flexShrink: 0,
} satisfies CSSProperties;

export type PeopleFilterBuilderProps = {
  /** The encoded `filters` search param. */
  filters: string | null;
  onChange: (serialized: JsonValue, isValid: boolean, isEmpty: boolean) => void;
};

type ConditionRowsProps = {
  typeOptions: TypeOptionGroup[];
};

function ConditionRows({ typeOptions }: ConditionRowsProps) {
  const tree = peopleFilterBuilder.useTree();
  const meta = peopleFilterBuilder.useMeta();
  const { addNode, setOperator, setTree } = peopleFilterBuilder.useBuilderActions();

  const handleAdd = (value: string | null) => {
    if (value == null) {
      return;
    }
    const type = peopleSearchConditionTypeOf(value);
    if (type != null) {
      addNode(tree.id, type);
    }
  };

  const rows = tree.children;

  return (
    <Stack gap="100">
      <InlineStack data-testid="dryad-group-operator" alignBlock="center" gap="100">
        <Select
          ariaLabel="Group operator"
          size="sm"
          value={tree.operator}
          options={GROUP_OPERATOR_OPTIONS}
          onChange={(value: string | null) => {
            if (value === 'and' || value === 'or') {
              setOperator(tree.id, value);
            }
          }}
          isDisabled={meta.isDisabled}
        />
        <Text color="subtle">of the following conditions match</Text>
        {rows.length > 0 ? (
          <Link
            data-testid="dryad-clear-filters"
            onClick={() => setTree({ kind: 'group', operator: 'and', children: [] })}
            isDisabled={meta.isDisabled}
          >
            Clear Filters
          </Link>
        ) : null}
      </InlineStack>

      {rows.length > 0 ? (
        <Stack gap="100">
          {rows.map((row, index) => (
            <GroupItemRow
              key={row.id}
              rowId={row.id}
              rowKind={row.kind}
              rowType={row.kind === 'leaf' ? row.type : null}
              index={index}
              showGutter={rows.length > 1}
              operator={tree.operator}
            />
          ))}
        </Stack>
      ) : null}

      <InlineStack alignBlock="center" gap="100">
        {/* A constant empty value keeps the select on its placeholder after each pick. */}
        <Select
          data-testid="dryad-group-add"
          ariaLabel="Add condition"
          size="sm"
          value=""
          placeholder="Add condition"
          options={typeOptions}
          onChange={handleAdd}
          isDisabled={meta.isDisabled}
        />
      </InlineStack>
    </Stack>
  );
}

// A registered view can opt out of the group's shared row chrome by declaring
// a static `ownsRowChrome = true`: the group then renders it without the
// shared remove button, and the row supplies its own remove affordance (via
// removeNode). For rows whose surface is a self-contained Pluma component
// (the read-only ConditionViewer sentence of seg-attr-row), the shared X
// would double up with the one the viewer's actions slot carries.
function viewOwnsRowChrome(view: LeafViewComponent | undefined): boolean {
  return view != null && 'ownsRowChrome' in view && view.ownsRowChrome === true;
}

type GroupItemRowProps = {
  rowId: string;
  rowKind: 'leaf' | 'group' | 'error';
  rowType: string | null;
  index: number;
  /** Whether the group renders the AND/OR gutter (more than one row). */
  showGutter: boolean;
  operator: string;
};

function GroupItemRow({ rowId, rowKind, rowType, index, showGutter, operator }: GroupItemRowProps) {
  const meta = peopleFilterBuilder.useMeta();
  const { removeNode } = peopleFilterBuilder.useBuilderActions();
  const View = peopleFilterBuilder.useViewForNode(rowType ?? '');
  const ownsChrome = rowKind === 'leaf' && viewOwnsRowChrome(View);

  return (
    <InlineStack data-testid={`dryad-group-item-${index}`} gap="100" alignBlock="start">
      {showGutter ? (
        <Box as="span" style={operatorGutterStyle}>
          {index !== 0 ? (
            <Box as="span" style={logicalOperatorLabelStyle} data-testid="dryad-logical-operator">
              {operator}
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Box flex={1} minW="0">
        {/* Error nodes (wire no plugin claims) and leaf types whose row hasn't
            been ported yet render read-only but stay removable, so a saved
            filter can't get stuck. */}
        {rowKind === 'leaf' && View != null ? (
          <View id={rowId} />
        ) : (
          <Text color="subtle">This condition can't be edited here yet.</Text>
        )}
      </Box>
      {ownsChrome ? null : (
        <Button
          data-testid="dryad-row-remove"
          aria-label="Remove condition"
          icon="close"
          isIconOnly
          variant="tertiary"
          size="sm"
          onClick={() => removeNode(rowId)}
          isDisabled={meta.isDisabled}
        />
      )}
    </InlineStack>
  );
}

/**
 * The interactive people-search condition builder. Edits commit live: every
 * builder change surfaces through onChange with the serialized wire and its
 * validity, and the page owns the debounce, the commit gate, and the URL
 * write.
 */
export function PeopleFilterBuilder({ filters, onChange }: PeopleFilterBuilderProps) {
  const { hasPermission } = usePermissions();
  const canViewAttributes = hasPermission('journeys/attribute_data/view');
  const environment = useEnvironmentOptional();

  const normalizedConditions = useMemo(() => normalizePeopleFilterWire(decodePeopleFiltersParam(filters)), [filters]);

  const builder = peopleFilterBuilder.useConditionBuilder({
    initialValue: normalizedConditions,
    format: PEOPLE_FILTER_FORMAT,
    // A fresh opt-out leaf defaults to the first channel with a configured
    // sender, which the plugin's own default (SMS) can't know. Thunk form so
    // the identities loading after the builder mounts are still picked up;
    // parsed leaves are never seeded.
    seeds: {
      [ConditionType.OptOut]: () => ({ channel: defaultOptOutChannel(environment.data?.identities ?? []) }),
    },
  });

  // Subscribed read: the attribute lock and the group chrome must follow
  // every tree change, including the fresh root Clear Filters mints.
  const tree = useSyncExternalStore(builder.sources.tree.subscribe, builder.sources.tree.get);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // The live-commit bridge: every builder change (row edits, add/remove,
  // clear, and the one notification an external filters change produces)
  // reaches the page, which gates and debounces the URL write.
  useEffect(() => {
    const output = builder.sources.output(PEOPLE_FILTER_FORMAT);
    return output.subscribe(() => {
      const { serialized, isValid, isEmpty } = output.get();
      onChangeRef.current(serialized, isValid, isEmpty);
    });
  }, [builder]);

  // The workspace gates the menu the same way the pre-builder search UI did:
  // device conditions need the push channel enabled, and phone-number
  // conditions need a configured SMS or WhatsApp sender.
  const pushEnabled = environment.data?.environment?.enabled_actions?.includes('push') === true;
  const optOutAvailable = hasAnyOptOutChannel(environment.data?.identities ?? []);

  const typeOptions: TypeOptionGroup[] = useMemo(() => {
    const gatedTypes = AVAILABLE_ROW_TYPES.filter((type) => {
      if (type === ATTRIBUTE_CONDITION_TYPE) {
        return canViewAttributes;
      }
      if (type === ConditionType.DeviceAttribute) {
        return pushEnabled;
      }
      if (type === ConditionType.OptOut) {
        return optOutAvailable;
      }
      return true;
    });
    return [
      {
        label: 'People',
        options: gatedTypes.map((type) => ({ value: type, label: PEOPLE_SEARCH_TYPE_LABELS.get(type) ?? type })),
      },
    ];
  }, [canViewAttributes, pushEnabled, optOutAvailable]);

  const meta: PeopleFilterBuilderMeta = {
    isDisabled: isAttributeLocked(tree, { canViewAttributes, disabled: false }),
    typeOptions,
    canViewAttributeValues: canViewAttributes,
  };

  return (
    <Box data-testid="people-filter-builder" w="full">
      <peopleFilterBuilder.DryadProvider builder={builder} meta={meta}>
        <ConditionRows typeOptions={typeOptions} />
      </peopleFilterBuilder.DryadProvider>
    </Box>
  );
}
